import React, { useState } from 'react';
import { supabase } from '../lib/supabase';
import Icon from './AppIcon';

/**
 * ReAuthModal
 * Prompts the admin to re-enter their password before performing a sensitive action.
 * Props:
 *   isOpen: boolean
 *   onSuccess: () => void  — called when re-auth succeeds
 *   onCancel: () => void   — called when user cancels
 *   actionLabel: string    — label for the action being protected (e.g. "Delete User")
 */
const ReAuthModal = ({ isOpen, onSuccess, onCancel, actionLabel = 'perform this action' }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!password?.trim()) {
      setError('Please enter your password.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user?.email) {
        setError('Session expired. Please log in again.');
        setLoading(false);
        return;
      }
      const { error: signInError } = await supabase?.auth?.signInWithPassword({
        email: user?.email,
        password,
      });
      if (signInError) {
        setError('Incorrect password. Please try again.');
      } else {
        setPassword('');
        setError('');
        onSuccess?.();
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setPassword('');
    setError('');
    onCancel?.();
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleCancel}
      />
      {/* Modal */}
      <div className="relative bg-surface border border-border rounded-2xl shadow-2xl w-full max-w-md p-6 z-10">
        {/* Header */}
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-warning/10 flex items-center justify-center flex-shrink-0">
            <Icon name="ShieldAlert" size={20} className="text-warning" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-text-primary">Confirm Your Identity</h2>
            <p className="text-sm text-text-secondary">
              Re-enter your password to {actionLabel}
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1.5">
              Admin Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => { setPassword(e?.target?.value); setError(''); }}
                placeholder="Enter your password"
                autoFocus
                className="w-full px-4 py-2.5 pr-10 bg-background border border-border rounded-lg text-text-primary placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors"
              />
              <button
                type="button"
                onClick={() => setShowPassword(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary hover:text-text-primary transition-colors"
                tabIndex={-1}
              >
                <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={16} />
              </button>
            </div>
            {error && (
              <p className="mt-1.5 text-sm text-error flex items-center gap-1.5">
                <Icon name="AlertCircle" size={14} />
                {error}
              </p>
            )}
          </div>

          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={handleCancel}
              className="flex-1 px-4 py-2.5 rounded-lg border border-border text-text-primary text-sm font-medium hover:bg-background transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2.5 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary/90 disabled:opacity-60 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Icon name="Loader2" size={15} className="animate-spin" />
                  Verifying…
                </>
              ) : (
                <>
                  <Icon name="ShieldCheck" size={15} />
                  Confirm
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReAuthModal;
